import { useMutation, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useAxios } from 'hooks/useAxios';
import { AxiosErrorHandler } from 'shared/Helpers';
import { BASE_URL_V2 } from 'shared/constants';
import {
  PartnerAclPartner,
  PartnerClientApplication,
  PartnerPermission
} from 'types/PartnerAclAdmin';

const partnerAclKeys = {
  all: ['partnerAcl'] as const,
  partners: () => [...partnerAclKeys.all, 'partners'] as const,
  permissionCatalog: () => [...partnerAclKeys.all, 'permissionCatalog'] as const,
  partnerPermissions: (partnerId: string) => [...partnerAclKeys.all, 'permissions', partnerId] as const,
  partnerApplication: (partnerId: string) => [...partnerAclKeys.all, 'application', partnerId] as const,
  partnerClient: (partnerId: string) => [...partnerAclKeys.all, 'client', partnerId] as const
};

const hasApplication = (app: PartnerClientApplication | null | undefined): app is PartnerClientApplication =>
  Boolean(app?.client_id);

export const usePartnerAclAdminApi = () => {
  const { axios } = useAxios(BASE_URL_V2);

  const listPartners = async (): Promise<PartnerAclPartner[]> => {
    try {
      const res = await axios.get('/admin/partner-acl/partners');
      if (res.status === 200) {
        return res.data;
      }
      return Promise.reject(Error(res?.data?.message || 'Failed to fetch partners'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  const listPermissionCatalog = async (): Promise<PartnerPermission[]> => {
    try {
      const res = await axios.get('/admin/partner-acl/permissions');
      if (res.status === 200) {
        return res.data;
      }
      return Promise.reject(Error(res?.data?.message || 'Failed to fetch partner permissions catalog'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  const listPartnerPermissions = async (partnerId: string): Promise<PartnerPermission[]> => {
    try {
      const res = await axios.get(`/admin/partner-acl/partners/${partnerId}/permissions`);
      if (res.status === 200) {
        return res.data;
      }
      return Promise.reject(Error(res?.data?.message || 'Failed to fetch partner permissions'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  const setPartnerPermissions = async (
    partnerId: string,
    partnerPermissionIds: string[]
  ): Promise<PartnerPermission[]> => {
    try {
      const res = await axios.put(`/admin/partner-acl/partners/${partnerId}/permissions`, {
        partner_permission_ids: partnerPermissionIds
      });
      if (res.status === 200) {
        return res.data;
      }
      return Promise.reject(Error(res?.data?.message || 'Failed to update partner permissions'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  const getPartnerApplication = async (partnerId: string): Promise<PartnerClientApplication | null> => {
    try {
      const res = await axios.get(`/admin/partner-acl/partners/${partnerId}/application`);
      if (res.status === 200) {
        return hasApplication(res.data) ? res.data : null;
      }
      return Promise.reject(Error(res?.data?.message || 'Failed to fetch partner application'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  const createPartnerApplication = async (partnerId: string): Promise<PartnerClientApplication> => {
    try {
      const res = await axios.post(`/admin/partner-acl/partners/${partnerId}/application`);
      if (res.status === 201) {
        return res.data;
      }
      return Promise.reject(Error(res?.data?.message || 'Failed to create partner application'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  const deletePartnerApplication = async (partnerId: string): Promise<void> => {
    try {
      const res = await axios.delete(`/admin/partner-acl/partners/${partnerId}/application`);
      if (res.status === 204) {
        return;
      }
      return Promise.reject(Error(res?.data?.message || 'Failed to delete partner application'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  const linkPartnerClient = async (partnerId: string, clientId: number): Promise<PartnerAclPartner> => {
    try {
      const res = await axios.put(`/admin/partner-acl/partners/${partnerId}/client`, {
        client_id: clientId
      });
      if (res.status === 200) {
        return res.data;
      }
      return Promise.reject(Error(res?.data?.message || 'Failed to link partner client'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  const unlinkPartnerClient = async (partnerId: string): Promise<PartnerAclPartner> => {
    try {
      const res = await axios.delete(`/admin/partner-acl/partners/${partnerId}/client`);
      if (res.status === 200) {
        return res.data;
      }
      return Promise.reject(Error(res?.data?.message || 'Failed to unlink partner client'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  const rotatePartnerApplicationSecret = async (partnerId: string): Promise<PartnerClientApplication> => {
    try {
      const res = await axios.post(`/admin/partner-acl/partners/${partnerId}/application/rotate-secret`);
      if (res.status === 200) {
        return res.data;
      }
      return Promise.reject(Error(res?.data?.message || 'Failed to rotate partner application secret'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  return {
    listPartners,
    listPermissionCatalog,
    listPartnerPermissions,
    setPartnerPermissions,
    getPartnerApplication,
    createPartnerApplication,
    deletePartnerApplication,
    rotatePartnerApplicationSecret,
    linkPartnerClient,
    unlinkPartnerClient
  };
};

export const usePartnerAclPartners = (config = {}): UseQueryResult<PartnerAclPartner[], AxiosError> => {
  const { listPartners } = usePartnerAclAdminApi();
  return useQuery({
    queryKey: partnerAclKeys.partners(),
    queryFn: listPartners,
    ...config
  });
};

export const usePartnerPermissionCatalog = (config = {}): UseQueryResult<PartnerPermission[], AxiosError> => {
  const { listPermissionCatalog } = usePartnerAclAdminApi();
  return useQuery({
    queryKey: partnerAclKeys.permissionCatalog(),
    queryFn: listPermissionCatalog,
    ...config
  });
};

export const usePartnerPermissions = (
  partnerId?: string,
  config = {}
): UseQueryResult<PartnerPermission[], AxiosError> => {
  const { listPartnerPermissions } = usePartnerAclAdminApi();
  return useQuery({
    queryKey: partnerAclKeys.partnerPermissions(partnerId ?? ''),
    queryFn: () => listPartnerPermissions(partnerId!),
    enabled: Boolean(partnerId),
    ...config
  });
};

export const usePartnerApplication = (
  partnerId?: string,
  config = {}
): UseQueryResult<PartnerClientApplication | null, AxiosError> => {
  const { getPartnerApplication } = usePartnerAclAdminApi();
  return useQuery({
    queryKey: partnerAclKeys.partnerApplication(partnerId ?? ''),
    queryFn: () => getPartnerApplication(partnerId!),
    enabled: Boolean(partnerId),
    ...config
  });
};

export const useSetPartnerPermissions = (partnerId?: string, config = {}) => {
  const { setPartnerPermissions } = usePartnerAclAdminApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (partnerPermissionIds: string[]) => setPartnerPermissions(partnerId!, partnerPermissionIds),
    onSuccess: (data) => {
      queryClient.setQueryData(partnerAclKeys.partnerPermissions(partnerId!), data);
    },
    ...config
  });
};

export const useCreatePartnerApplication = (partnerId?: string, config = {}) => {
  const { createPartnerApplication } = usePartnerAclAdminApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => createPartnerApplication(partnerId!),
    onSuccess: (data) => {
      queryClient.setQueryData(partnerAclKeys.partnerApplication(partnerId!), data);
      queryClient.invalidateQueries(partnerAclKeys.partners());
    },
    ...config
  });
};

export const useDeletePartnerApplication = (partnerId?: string, config = {}) => {
  const { deletePartnerApplication } = usePartnerAclAdminApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => deletePartnerApplication(partnerId!),
    onSuccess: () => {
      queryClient.setQueryData(partnerAclKeys.partnerApplication(partnerId!), null);
      queryClient.invalidateQueries(partnerAclKeys.partners());
    },
    ...config
  });
};

export const useLinkPartnerClient = (partnerId?: string, config = {}) => {
  const { linkPartnerClient } = usePartnerAclAdminApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (clientId: number) => linkPartnerClient(partnerId!, clientId),
    onSuccess: (data) => {
      queryClient.setQueryData(partnerAclKeys.partners(), (current: PartnerAclPartner[] | undefined) =>
        current?.map((partner) => (partner.id === data.id ? data : partner))
      );
    },
    ...config
  });
};

export const useUnlinkPartnerClient = (partnerId?: string, config = {}) => {
  const { unlinkPartnerClient } = usePartnerAclAdminApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => unlinkPartnerClient(partnerId!),
    onSuccess: (data) => {
      queryClient.setQueryData(partnerAclKeys.partners(), (current: PartnerAclPartner[] | undefined) =>
        current?.map((partner) => (partner.id === data.id ? data : partner))
      );
    },
    ...config
  });
};

export const useRotatePartnerApplicationSecret = (partnerId?: string, config = {}) => {
  const { rotatePartnerApplicationSecret } = usePartnerAclAdminApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => rotatePartnerApplicationSecret(partnerId!),
    onSuccess: (data) => {
      queryClient.setQueryData(partnerAclKeys.partnerApplication(partnerId!), data);
    },
    ...config
  });
};
